import React, { Suspense } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'

import { ArrowLeftIcon, ArrowRightIcon, FileQuestionIcon } from 'lucide-react'

import { Box, Link, Stack, Typography, useTheme } from '@mui/material'

import { Button, Card } from '@repo/ui'
import { EmptyState } from 'components'

import type { DocEntry } from 'docs'
import { CUSTOMER_DOCS } from 'docs/sources'
import { useCustomerDocs } from 'docs/useCustomerDocs'
import type { DocsSource } from 'docs/source'

import { DocsLayout } from './DocsLayout'
import { DocProseSkeleton } from './DocProseSkeleton'
import { DocToc } from './DocToc'

/**
 * Outer bound for the whole three-column slab. Above this the columns stop spreading and
 * the block centres instead — see the note at the container.
 */

const NeighbourCard = ({
  doc,
  direction,
  to,
}: {
  doc: DocEntry
  direction: 'prev' | 'next'
  to: string
}) => {
  const theme = useTheme()
  const isNext = direction === 'next'
  return (
    <Link
      component={RouterLink}
      to={to}
      sx={{ display: 'block', textDecoration: 'none', borderRadius: 3, flex: 1 }}
    >
      {/* Action row, not a selectable card: it navigates immediately and holds no
          selected state, so it takes the brand border + focus ring treatment (§34). */}
      <Card
        sx={{
          // `margin`/`padding` rather than the `m`/`p` shorthands: Card sets the long
          // form, and MUI resolves sx keys by its own order, not object order — the
          // shorthand would not reliably win.
          margin: 0,
          padding: 4,
          height: '100%',
          transition: 'border-color 120ms ease, box-shadow 120ms ease',
          '&:hover, &:focus-visible': {
            borderColor: theme.palette.brand[600],
            boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%']}`,
          },
        }}
      >
        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          justifyContent={isNext ? 'flex-end' : 'flex-start'}
        >
          {!isNext && <ArrowLeftIcon strokeWidth={1} size={18} />}
          <Stack spacing={1} sx={{ textAlign: isNext ? 'right' : 'left' }}>
            <Typography variant="body4" color="text.secondary">
              {isNext ? 'Next' : 'Previous'}
            </Typography>
            <Typography variant="body3" color="text.primary">
              {doc.title}
            </Typography>
          </Stack>
          {isNext && <ArrowRightIcon strokeWidth={1} size={18} />}
        </Stack>
      </Card>
    </Link>
  )
}

/**
 * A single published guide: section nav on the left, the guide in the middle, its own
 * table of contents on the right. Below the breakpoint both side columns collapse — the
 * section nav to a pill row above the prose, the table of contents away entirely, since
 * on a narrow screen scrolling is the table of contents.
 *
 * Renders whichever docs source it is handed (VRG-224). The renderer arrives with the
 * source as `source.Content`, always a `lazy()`: the markdown renderer plus its body map
 * is the heaviest thing on this route and the only part not needed to paint it — the
 * nav, title, and table of contents all come from the manifest in the main bundle.
 * Splitting it keeps that weight off every other page, gives the prose column a real
 * loading state, and is what stops internal guide text travelling in a customer's chunk.
 */
export const DocPage = ({
  source,
  overview = false,
}: {
  source: DocsSource
  /**
   * Render this source's overview guide instead of resolving one from the URL. This is
   * what `/docs` is: the same reader layout, on the guide named by `source.overviewSlug`,
   * so the landing page has a left nav and a table of contents like every other page
   * rather than being a differently-shaped grid.
   */
  overview?: boolean
}) => {
  const { category, slug } = useParams()
  const navigate = useNavigate()
  const overviewDoc = source.overviewSlug
    ? source.docs.find(d => d.slug === source.overviewSlug)
    : undefined
  const doc = overview ? overviewDoc : source.findDoc(category, slug)

  if (!doc) {
    // The empty corpus / missing-overview case, which VRG-402 made reachable: this route is
    // now the docs INDEX for both sites, so `overview` with no `overviewDoc` is a landing
    // page with nothing to land on.
    //
    // Rendered inside DocsLayout rather than as a bare page, and this is the whole point —
    // the contents list stays on screen, so the reader can still choose a guide. The previous
    // version rendered a bare `Header` plus a "Browse docs" button pointing at
    // `source.indexPath`, which on the index route is the page you are already on: a dead
    // button on a page with no navigation. An empty state must never be a cul-de-sac.
    const emptyCorpus = source.docs.length === 0
    // The headline stays the SITE's name, not a restatement of the empty state below it —
    // rendering "This page doesn't exist" as both the h1 and the empty-state title said the
    // same sentence twice.
    return (
      <DocsLayout source={source} headline={source.label}>
        <EmptyState
          icon={<FileQuestionIcon strokeWidth={1} size={28} />}
          title={emptyCorpus ? 'Nothing published yet' : "This page doesn't exist"}
          text={
            emptyCorpus
              ? 'No guides have been published to this site yet.'
              : 'The page may have been renamed or is not published yet. Choose one from the contents on the left.'
          }
          // Only where it leads somewhere the reader is not already standing.
          buttons={
            overview ? undefined : (
              <Button onClick={() => navigate(source.indexPath)}>Browse docs</Button>
            )
          }
        />
      </DocsLayout>
    )
  }

  const { prev, next } = source.neighbours(doc)
  const { Content } = source

  // Push the anchor onto the URL rather than scrolling directly: DocContent already
  // reacts to the hash, so this keeps one scroll path for TOC clicks and inbound links,
  // and leaves the reader with a copyable link to the section they are on.
  const goToHeading = (id: string) =>
    navigate(`${source.docPath(doc.category, doc.slug)}#${id}`)

  return (
    // The three columns are a CENTRED SLAB, not the full viewport. Unconstrained, the nav
    // pinned to the left edge and the TOC to the right, so at 2078px they sat ~1500px
    // apart with the prose stranded between them — the page stopped reading as a document
    // and started reading as a dashboard. Settings solved the same problem with
    // NARROW_COLUMN_WIDTH (1100); docs need more because it carries three columns rather
    // than one, so the prose column lands at a similar measure once the two rails are
    // paid for. Inert below the max — at 1280 nothing moves.
    <DocsLayout source={source} current={doc} headline={doc.headline}
      aside={<DocToc headings={doc.headings} scrollKey={doc.slug} onSelect={goToHeading} />}
    >
          <Suspense fallback={<DocProseSkeleton />}>
            <Content doc={doc} />
          </Suspense>

          <Typography variant="body4" color="text.secondary" sx={{ display: 'block', mt: 10 }}>
            Last reviewed {doc.lastReviewed}
          </Typography>

          {(prev || next) && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mt: 4 }}>
              {prev ? (
                <NeighbourCard
                  doc={prev}
                  direction="prev"
                  to={source.docPath(prev.category, prev.slug)}
                />
              ) : (
                <Box sx={{ flex: 1 }} />
              )}
              {next ? (
                <NeighbourCard
                  doc={next}
                  direction="next"
                  to={source.docPath(next.category, next.slug)}
                />
              ) : (
                <Box sx={{ flex: 1 }} />
              )}
            </Stack>
          )}
    </DocsLayout>
  )
}

/**
 * VRG-680 — the customer `/docs` routes, gated on the API's launch switches. A guide tagged
 * `feature:` whose switch is off is absent from the catalog, so its route renders the same
 * not-found state as an unpublished guide. While `/features` is still answering, a gated
 * guide's route renders nothing rather than flashing "not found" at a brand who can see it.
 */
export const CustomerDocPage = ({ overview }: { overview?: boolean }) => {
  const { source, isKnown } = useCustomerDocs()
  const { category, slug } = useParams()
  if (!isKnown && !overview && CUSTOMER_DOCS.findDoc(category, slug)?.feature) return null
  return <DocPage source={source} overview={overview} />
}
